import { useEffect, useState } from 'react'
import { CalendarDays, Copy, RotateCcw, Check, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useOrg } from '@/context/OrgContext'
import { useAllTours } from '@/hooks/useActivities'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { Drawer, DrawerContent } from '@/components/ui/drawer'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { TourStatusChip } from '@/components/TourStatus'
import { AppleIcon } from '@/components/icons/AppleIcon'
import { GoogleIcon } from '@/components/icons/GoogleIcon'
import { ensureIcalToken, regenerateIcalToken, buildFeedUrl } from '@/lib/icalFeed'
import type { Tour } from '@/types'
import { cn } from '@/lib/utils'

// Google's documented "add calendar from URL" deep link. Opens Google
// Calendar with the confirmation prompt pre-filled — user just clicks
// "Add" and the subscription is live.
function googleCalendarAddUrl(feedUrl: string): string {
  return `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(feedUrl)}`
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CalendarFeedDialog({ open, onOpenChange }: Props) {
  const isDesktop = useMediaQuery('(min-width: 640px)')
  const body = <CalendarFeedBody onClose={() => onOpenChange(false)} open={open} />

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>Calendar feed</DialogTitle>
            <DialogDescription>Subscribe to your activities from Apple or Google Calendar.</DialogDescription>
          </DialogHeader>
          {body}
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[88dvh]">
        <div className="overflow-y-auto">{body}</div>
      </DrawerContent>
    </Drawer>
  )
}

function CalendarFeedBody({ onClose, open }: { onClose: () => void; open: boolean }) {
  const { activeOrg } = useOrg()
  const { data: tours = [], isLoading: toursLoading } = useAllTours()
  const [tokens, setTokens] = useState<Record<string, string>>({})
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set())
  const [regeneratingIds, setRegeneratingIds] = useState<Set<string>>(new Set())

  // Only surface active tours. Archived tours still have their data but
  // most users don't want a live subscription to a finished gira.
  const activeTours = tours.filter(t => t.estado === 'active')

  // Fetch tokens for all active tours the first time the dialog opens.
  useEffect(() => {
    if (!open || !activeOrg || activeTours.length === 0) return
    const missing = activeTours.filter(t => !tokens[t.id] && !loadingIds.has(t.id))
    if (missing.length === 0) return

    setLoadingIds(prev => {
      const next = new Set(prev)
      missing.forEach(t => next.add(t.id))
      return next
    })

    Promise.allSettled(
      missing.map(async t => {
        try {
          const token = await ensureIcalToken(t.id)
          return { tourId: t.id, token }
        } catch (err) {
          console.error('ensureIcalToken failed for', t.id, err)
          return null
        }
      })
    ).then(results => {
      const next: Record<string, string> = {}
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value) next[r.value.tourId] = r.value.token
      }
      setTokens(prev => ({ ...prev, ...next }))
      setLoadingIds(prev => {
        const n = new Set(prev)
        missing.forEach(t => n.delete(t.id))
        return n
      })
    })
  }, [open, activeOrg, activeTours, tokens, loadingIds])

  async function handleRegenerate(tourId: string) {
    const confirmed = window.confirm(
      'Regenerating will invalidate the current URL. Anyone already subscribed will stop receiving updates until they re-subscribe. Continue?'
    )
    if (!confirmed) return
    setRegeneratingIds(prev => new Set(prev).add(tourId))
    try {
      const token = await regenerateIcalToken(tourId)
      setTokens(prev => ({ ...prev, [tourId]: token }))
      toast.success('New calendar URL generated')
    } catch (err) {
      console.error(err)
      toast.error('Could not regenerate URL')
    } finally {
      setRegeneratingIds(prev => {
        const n = new Set(prev)
        n.delete(tourId)
        return n
      })
    }
  }

  const pending = toursLoading || activeTours.some(t => !tokens[t.id] && loadingIds.has(t.id))

  return (
    <div className="flex flex-col max-h-[88dvh]">
      <div className="px-5 pt-3 pb-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--subtle-foreground)]">
          Calendar feed
        </p>
        <h2 className="text-[24px] font-semibold leading-[1.1] tracking-[-0.02em] mt-1.5 text-foreground">
          Sync to your calendar
        </h2>
        <p className="text-[13px] text-muted-foreground mt-1.5 leading-relaxed">
          Copy the link into Apple Calendar, Google Calendar o Outlook. El
          feed solo incluye actividades que vos podés ver.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto border-t border-border">
        {pending && activeTours.length === 0 ? (
          <div className="p-5 space-y-3">
            <Skeleton className="h-20 rounded-xl" />
            <Skeleton className="h-20 rounded-xl" />
          </div>
        ) : activeTours.length === 0 ? (
          <p className="text-sm text-muted-foreground italic px-5 py-10 text-center">
            No active tours to subscribe to.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {activeTours.map(t => (
              <TourFeedRow
                key={t.id}
                tour={t}
                token={tokens[t.id]}
                loading={loadingIds.has(t.id) && !tokens[t.id]}
                regenerating={regeneratingIds.has(t.id)}
                onRegenerate={() => handleRegenerate(t.id)}
              />
            ))}
          </ul>
        )}
      </div>

      <div className="px-5 pt-3 pb-4 border-t border-border flex gap-2 pb-safe bg-[color:var(--background)]">
        <button type="button" onClick={onClose} className="login-btn compact flex-1">
          Close
        </button>
      </div>
    </div>
  )
}

function TourFeedRow({
  tour, token, loading, regenerating, onRegenerate,
}: {
  tour: Tour
  token: string | undefined
  loading: boolean
  regenerating: boolean
  onRegenerate: () => void
}) {
  const [copied, setCopied] = useState(false)
  const webcalUrl = token ? buildFeedUrl(token, 'webcal') : ''
  const httpsUrl = token ? buildFeedUrl(token, 'https') : ''
  const googleUrl = token ? googleCalendarAddUrl(webcalUrl) : ''

  async function copyHttps() {
    try {
      await navigator.clipboard.writeText(httpsUrl)
      setCopied(true)
      toast.success('URL copied')
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error('Could not copy. Select the URL manually.')
    }
  }

  return (
    <li className="px-5 py-4 space-y-3">
      <div className="flex items-center gap-2">
        <CalendarDays className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={2} />
        <span className="text-sm font-semibold text-foreground flex-1 truncate">{tour.nombre}</span>
        <TourStatusChip tour={tour} />
      </div>

      {loading || !token ? (
        <Skeleton className="h-10 rounded-lg" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            {/* Apple Calendar — webcal:// scheme. iOS/macOS interceptan y
                abren Calendar.app con el prompt "Add Subscription". */}
            <a
              href={webcalUrl}
              className="inline-flex items-center justify-center gap-2 h-10 rounded-lg border border-border-strong bg-card hover:bg-card-2 px-3 text-sm font-semibold text-foreground transition-colors"
            >
              <AppleIcon className="w-4 h-4" />
              Apple Calendar
            </a>
            {/* Google Calendar — deep link oficial (render?cid=). Google
                NO tiene URL scheme nativo para agregar suscripciones desde
                la app; el flujo pasa por el web siempre. Sin target=_blank
                para no abrir una pestaña nueva: navega en la misma, menos
                fricción. Usuarios en la PWA iOS igual son escapados a
                Safari por la política de Apple. */}
            <a
              href={googleUrl}
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 h-10 rounded-lg border border-border-strong bg-card hover:bg-card-2 px-3 text-sm font-semibold text-foreground transition-colors"
            >
              <GoogleIcon className="w-4 h-4" />
              Google Calendar
            </a>
          </div>

          {/* Secondary actions: copy la https (universal, sirve para
              Outlook u otros) + regenerate. */}
          <div className="flex items-center justify-between pt-0.5">
            <button
              type="button"
              onClick={copyHttps}
              className={cn(
                'inline-flex items-center gap-1.5 text-[11px] font-semibold transition-colors',
                copied ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
              )}
              aria-label="Copy calendar URL"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? 'Copied' : 'Copy URL'}
            </button>
            <button
              type="button"
              onClick={onRegenerate}
              disabled={regenerating}
              className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              {regenerating ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RotateCcw className="w-3 h-3" />
              )}
              Regenerate
            </button>
          </div>
        </>
      )}
    </li>
  )
}
